import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import useAuthUser from '../services/useAuthUser';

import { getCognidoorApiBase } from '../config/apiBase';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

export default function InternalReviewsPage() {
  const { user } = useAuthUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${getCognidoorApiBase()}/api/internal/reviews?limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to load reviews');
        }
        if (!cancelled) setItems(Array.isArray(payload?.items) ? payload.items : []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load reviews');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6">
      <SEO
        title="Internal Reviews"
        description="Internal owner-only review moderation surface."
        url="/internal/reviews"
        noIndex={true}
      />
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Internal Reviews</h1>
          <p className="text-sm text-slate-600 mt-1">Owner-only review collection surface.</p>
        </div>

        {loading ? <div className="text-slate-600">Loading reviews...</div> : null}
        {error ? <div className="text-red-600">{error}</div> : null}
        {!loading && !error && items.length === 0 ? (
          <div className="text-slate-600">No reviews submitted yet.</div>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900">{item.product_label || item.product_key}</p>
                    <p className="text-xs text-slate-500">Submitted {formatDate(item.submitted_at || item.created_at)}</p>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                    Rating {Number(item.rating).toFixed(1)}
                  </span>
                </div>
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{item.review_text || 'No comment provided.'}</p>
                <div className="mt-3 text-xs text-slate-500">
                  <span>User: {item.user_email || item.user_id}</span>
                  <span className="ml-3">Source: {item.source_surface || 'unknown'}</span>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    disabled
                    title="Coming soon"
                    className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-500 cursor-not-allowed"
                  >
                    Push to landing page (coming soon)
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
